import { useCallback, useState } from "react";
import { serializeAreaCoordinates, validateAreaCoordinates } from "./aiMappingGeo";
import { useCreateAiMappingRunArea } from "./hooks";
import { AiMappingAreaLike } from "./aiMappingTypes";
import { AiMappingDrawSession, AiMappingNotify } from "./aiMappingControllerTypes";

interface UseAiMappingAreaDrawingOptions {
  run: { id: string } | null;
  driver: {
    startDraw: (
      inputType: "BBOX" | "POLYGON",
      onDrawn: (points: import("./aiMappingGeo").LatLngPair[]) => void,
      onCancel?: () => void
    ) => void;
    cancelDraw: () => void;
    clearDraft: () => void;
  } | null;
  defaultAreaName?: string | null;
  unmountedRef: React.MutableRefObject<boolean>;
  notify: AiMappingNotify;
  handleError: (error: unknown, options?: { inline?: boolean }) => boolean;
  updateAreaInRun: (area: AiMappingAreaLike) => void;
  onAreaCreated: (areaId: string) => void;
}

export function useAiMappingAreaDrawing({
  run,
  driver,
  defaultAreaName,
  unmountedRef,
  notify,
  handleError,
  updateAreaInRun,
  onAreaCreated,
}: UseAiMappingAreaDrawingOptions) {
  const [drawSession, setDrawSession] = useState<AiMappingDrawSession | null>(null);
  const [savingArea, setSavingArea] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [createAreaAction] = useCreateAiMappingRunArea();

  const startDrawing = useCallback(
    (inputType: "BBOX" | "POLYGON") => {
      if (!driver) return;
      setFormError(null);
      setDrawSession({ inputType, points: null });
      driver.startDraw(
        inputType,
        (points) => {
          setDrawSession((session) => (session ? { ...session, points } : null));
        },
        () => setDrawSession(null)
      );
    },
    [driver]
  );

  const cancelDrawing = useCallback(() => {
    driver?.cancelDraw();
    setDrawSession(null);
    setFormError(null);
  }, [driver]);

  const saveArea = useCallback(
    async (nameOverride?: string): Promise<boolean> => {
      if (!run || !drawSession) return false;
      const points = drawSession.points ?? [];
      const validationError = validateAreaCoordinates(drawSession.inputType, points);
      if (validationError) {
        setFormError(validationError);
        return false;
      }
      setSavingArea(true);
      setFormError(null);
      try {
        const result = await createAreaAction({
          variables: {
            input: {
              runId: run.id,
              name: nameOverride?.trim() || defaultAreaName?.trim() || "Untitled Area",
              inputType: drawSession.inputType as any,
              coordinates: serializeAreaCoordinates(points),
            },
          },
        });
        const created = result.data?.createAiMappingRunArea as AiMappingAreaLike | undefined;
        if (created && !unmountedRef.current) {
          updateAreaInRun(created);
          onAreaCreated(created.id);
          driver?.clearDraft();
          setDrawSession(null);
          notify("Measurement saved", "success");
        }
        return true;
      } catch (error) {
        if (!unmountedRef.current) handleError(error, { inline: true });
        return false;
      } finally {
        if (!unmountedRef.current) setSavingArea(false);
      }
    },
    [
      run,
      drawSession,
      defaultAreaName,
      createAreaAction,
      updateAreaInRun,
      onAreaCreated,
      driver,
      notify,
      handleError,
      unmountedRef,
    ]
  );

  const cancelDraft = useCallback(() => {
    driver?.clearDraft();
    setDrawSession(null);
  }, [driver]);

  return {
    drawSession,
    savingArea,
    formError,
    setFormError,
    startDrawing,
    cancelDrawing,
    saveArea,
    cancelDraft,
  };
}
